import { parseCalendarPlacementDay, type InitialCalendarPlacement } from "../calendarPlacement";
import type { ScheduleScope } from "../scheduleConfig";

// The `/schedule` URL is navigation/context only: which project, which
// mode, which shorts, which short/post is focused, where the user came from.
// Unsaved scheduling edits never live here — they stay in the editor's
// memory until committed or discarded.

export const SCHEDULE_MODES = ["create", "edit"] as const;
export type ScheduleMode = (typeof SCHEDULE_MODES)[number];

export const SCHEDULE_FROMS = ["results", "calendar", "editor", "spaces"] as const;
export type ScheduleFrom = (typeof SCHEDULE_FROMS)[number];

export type ScheduleUrlState = {
  /** Applied once when a calendar creation session hydrates. */
  initialPlacement?: InitialCalendarPlacement;
  /** Exactly one resource owns the session; focus never changes ownership. */
  project: string | null;
  standalone?: string;
  mode: ScheduleMode;
  /** Create mode: the entry shorts, in order. Empty in edit mode. */
  videos: string[];
  /** The focused short (edit mode: the entry short). */
  video: string | null;
  /** The focused account post. */
  post: string | null;
  from: ScheduleFrom | null;
  /** yyyy-MM-dd — the calendar month to return to. */
  returnDate: string | null;
};

export const MAX_SCHEDULE_URL_VIDEOS = 100;

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isValidScheduleId = (value: unknown): value is string =>
  typeof value === "string" && ID_PATTERN.test(value);

const isMode = (value: string | null): value is ScheduleMode =>
  (SCHEDULE_MODES as readonly string[]).includes(value ?? "");
const isFrom = (value: string | null): value is ScheduleFrom =>
  (SCHEDULE_FROMS as readonly string[]).includes(value ?? "");
const validIdOrNull = (value: string | null) =>
  isValidScheduleId(value) ? value : null;
const validDateOrNull = (value: string | null) =>
  value && DATE_PATTERN.test(value) ? value : null;

/** Syntactically valid, deduped, capped id list from a comma-joined param. */
export const parseScheduleVideoIds = (raw: string | null): string[] => {
  if (!raw) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const part of raw.split(",")) {
    const id = part.trim();
    if (!isValidScheduleId(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= MAX_SCHEDULE_URL_VIDEOS) break;
  }
  return ids;
};

/**
 * Requires one owner and a mode. Folder create links need a video roster,
 * and folder edit links need a video. Standalone links use their owner as
 * their one-item roster and default focus.
 */
export const parseScheduleUrlState = (
  params: URLSearchParams,
): ScheduleUrlState | null => {
  const project = validIdOrNull(params.get("project"));
  const standalone = validIdOrNull(params.get("standalone"));
  const mode = params.get("mode");
  if (!isMode(mode) || params.has("project") === params.has("standalone") ||
    params.getAll("project").length > 1 || params.getAll("standalone").length > 1 ||
    (params.has("project") ? !project : !standalone)) return null;
  const selectedVideos = parseScheduleVideoIds(params.get("videos"));
  const focusedVideo = validIdOrNull(params.get("video"));
  if (standalone && ((focusedVideo && focusedVideo !== standalone) || selectedVideos.some(id => id !== standalone))) return null;
  const videos = standalone ? [standalone] : selectedVideos;
  const video = focusedVideo ?? standalone;
  if (mode === "create" && videos.length === 0) return null;
  if (mode === "edit" && !video) return null;
  const from = params.get("from");
  let initialPlacement: InitialCalendarPlacement | undefined;
  if (params.has("day")) {
    const day = params.get("day");
    if (mode !== "create" || from !== "calendar" ||
      params.getAll("day").length !== 1 || !day || !parseCalendarPlacementDay(day)) return null;
    initialPlacement = { day };
  }
  return {
    ...(initialPlacement ? { initialPlacement } : {}),
    project,
    ...(standalone ? { standalone } : {}),
    mode,
    videos: mode === "create" ? videos : [],
    video: video ?? (mode === "create" ? videos[0] : null),
    post: validIdOrNull(params.get("post")),
    from: isFrom(from) ? from : null,
    returnDate: validDateOrNull(params.get("returnDate")),
  };
};

export type ScheduleHrefInput = {
  initialPlacement?: InitialCalendarPlacement;
  project?: string | null;
  standalone?: string;
  mode: ScheduleMode;
  videos?: readonly string[];
  video?: string | null;
  post?: string | null;
  from?: ScheduleFrom | null;
  returnDate?: string | null;
};

/** Every generated `/schedule` URL carries one owner and a mode. */
export const buildScheduleHref = (input: ScheduleHrefInput): string => {
  const params = new URLSearchParams();
  if (Boolean(input.project) === Boolean(input.standalone)) throw new Error("A schedule needs exactly one owner");
  if (input.standalone) params.set("standalone", input.standalone);
  else params.set("project", input.project!);
  params.set("mode", input.mode);
  const videos = input.standalone ? [input.standalone] : parseScheduleVideoIds((input.videos ?? []).join(","));
  if (input.mode === "create" && videos.length > 0) {
    params.set("videos", videos.join(","));
  }
  const video = validIdOrNull(input.video ?? null) ?? videos[0] ?? null;
  if (video) params.set("video", video);
  const post = validIdOrNull(input.post ?? null);
  if (post) params.set("post", post);
  if (input.from) params.set("from", input.from);
  const returnDate = validDateOrNull(input.returnDate ?? null);
  if (returnDate) params.set("returnDate", returnDate);
  if (input.initialPlacement) {
    const placement = input.initialPlacement;
    if (input.mode !== "create" || input.from !== "calendar" || videos.length === 0 ||
      !parseCalendarPlacementDay(placement.day)) throw new Error("Invalid calendar placement");
    params.set("day", placement.day);
  }
  return `/schedule?${params.toString()}`;
};

const todayKey = (now: Date) => {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const calendarHrefForDate = (date: string | null, now = new Date()) =>
  `/calendar?currentDate=${date ?? todayKey(now)}`;

type ReturnHrefState = Pick<
  ScheduleUrlState,
  "project" | "standalone" | "video" | "videos" | "from" | "returnDate"
>;

/**
 * Where a session returns to. Defensive: never an `undefined` segment,
 * never `#undefined`. Results/spaces return to the project page, with the
 * focus short as the hash only when one exists; editor returns to the
 * editor only with a usable video id, else to the project page.
 */
export const resolveReturnHref = (
  state: ReturnHrefState,
  options?: { videoId?: string | null; now?: Date },
): string => {
  const focus =
    validIdOrNull(options?.videoId ?? null) ??
    validIdOrNull(state.video) ??
    state.videos.find(isValidScheduleId) ??
    null;
  const project = validIdOrNull(state.project);
  const resourceId = project ?? validIdOrNull(state.standalone ?? null);
  switch (state.from) {
    case "results":
    case "spaces":
      if (!resourceId) return "/calendar";
      return project && focus ? `/spaces/${project}#${focus}` : `/spaces/${resourceId}`;
    case "calendar":
      return calendarHrefForDate(state.returnDate, options?.now);
    case "editor":
      if (focus) return `/editor/${focus}`;
      return resourceId ? `/spaces/${resourceId}` : "/calendar";
    default:
      return "/calendar";
  }
};

/**
 * The one fallback for a malformed `/schedule` URL: a trustworthy source
 * href when the remaining params allow it, else `/calendar`. Never infers
 * a project or a mode.
 */
export const resolveMalformedScheduleFallback = (
  params: URLSearchParams,
  now = new Date(),
): string => {
  const from = params.get("from");
  const project = validIdOrNull(params.get("project"));
  const video = validIdOrNull(params.get("video"));
  const resourceId = project ?? validIdOrNull(params.get("standalone"));
  if ((from === "results" || from === "spaces") && resourceId) {
    return `/spaces/${resourceId}`;
  }
  if (from === "calendar") {
    return calendarHrefForDate(validDateOrNull(params.get("returnDate")), now);
  }
  if (from === "editor" && video) {
    return `/editor/${video}`;
  }
  return "/calendar";
};

export const scheduleScopeFromUrl = (state: Pick<ScheduleUrlState, "project" | "standalone">): ScheduleScope => {
  if (state.project) return { kind: "folder", id: state.project };
  if (state.standalone) return { kind: "video", id: state.standalone };
  throw new Error("A schedule needs an owner");
};

export function canonicalScheduleHref(state: ScheduleUrlState, video: { id: string; folderId?: string | null }): string | null {
  if (!state.standalone || !video.folderId) return null;
  return buildScheduleHref({ ...state, standalone: undefined, project: video.folderId, video: video.id, videos: [video.id] });
}
